// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — Bulk Actions constants
//
// Per-entity op metadata for the bulk-action UI. The `field` name and every op
// `value` MUST match the backend's exact single-record validation lists
// (see `api-v2/bulk.js`: LEAD_STATUSES, DEAL_STAGES, TASK_STATUSES). Labels are
// display-only and reuse the existing per-feature label maps where possible.
// (Requirements 8.1, 8.2)
// ─────────────────────────────────────────────────────────────────────────────

import type { BulkEntity, BulkOpOption } from './types';

/** Human-readable field the op targets, used in the bar's prompt (e.g. "Set status"). */
export const BULK_OP_FIELD_LABEL: Record<BulkEntity, string> = {
  lead: 'status',
  deal: 'stage',
  task: 'status',
};

/**
 * Selectable target values per entity. Values mirror the backend allowlists
 * verbatim; a value the single-record endpoint would reject is never offered.
 */
export const BULK_OP_OPTIONS: Record<BulkEntity, BulkOpOption[]> = {
  // Lead_Status — mirrors `PATCH /api/leads/:id` whitelist.
  lead: [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'interested', label: 'Interested' },
    { value: 'converted', label: 'Converted' },
    { value: 'rejected', label: 'Rejected' },
  ],
  // Deal_Stage — mirrors `DEAL_STAGES` in server.js.
  deal: [
    { value: 'prospecting', label: 'Prospecting' },
    { value: 'qualification', label: 'Qualification' },
    { value: 'proposal', label: 'Proposal' },
    { value: 'negotiation', label: 'Negotiation' },
    { value: 'closed_won', label: 'Closed Won' },
    { value: 'closed_lost', label: 'Closed Lost' },
  ],
  // Task status — mirrors `PATCH /api/tasks/:id` whitelist.
  task: [
    { value: 'todo', label: 'To Do' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'review', label: 'Review' },
    { value: 'done', label: 'Done' },
  ],
};

/**
 * The TanStack Query key each entity's list uses, so a successful bulk op can
 * invalidate the correct cache and refresh the updated rows. Matches the keys
 * used by the existing feature hooks (`useLeads`, `useDeals`, `useTasks`).
 */
export const BULK_LIST_QUERY_KEY: Record<BulkEntity, string> = {
  lead: 'leads',
  deal: 'deals',
  task: 'tasks',
};

/** Human-readable noun for each entity, used in bar/result copy. */
export const BULK_ENTITY_NOUN: Record<BulkEntity, string> = {
  lead: 'lead',
  deal: 'deal',
  task: 'task',
};
